import { QUERIES_KEYS } from "queries";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { coursegroupAttributesApi } from "./coursegroupAttributesApi";

export const useCourseGroupAttributes = (
  skip: number,
  limit: number,
  searchPhrase: string = "",
  orderBy: string = "",
) => {
  const queryClient = useQueryClient();
  const { isLoading, isFetching, data } = useQuery({
    queryKey: QUERIES_KEYS.COURSEGROUP_ATTRIBUTES(skip, limit, searchPhrase, orderBy),
    queryFn: () => coursegroupAttributesApi.fetch(skip, limit, searchPhrase, orderBy),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: QUERIES_KEYS.COURSEGROUP_ATTRIBUTES(skip, limit, searchPhrase, orderBy),
    });
  };

  return {
    invalidate,
    isLoading,
    isFetching,
    attributes: data?.data.data ?? [],
    total: data?.data.total ?? 0,
  };
};

export const useCourseGroupAttribute = (id: string | undefined) => {
  const queryClient = useQueryClient();
  const { isLoading, data } = useQuery({
    queryKey: QUERIES_KEYS.COURSEGROUP_ATTRIBUTE_BY_ID(id ?? ""),
    queryFn: () => coursegroupAttributesApi.fetchById(Number(id)),
    enabled: !!id,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: QUERIES_KEYS.COURSEGROUP_ATTRIBUTE_BY_ID(id ?? ""),
    });
  };

  return {
    invalidate,
    isLoading,
    attribute: data?.data ?? null,
  };
};

export const useCourseGroupAttributeValues = (courseGroupId: string | undefined) => {
  const queryClient = useQueryClient();
  const { isLoading, data } = useQuery({
    queryKey: QUERIES_KEYS.COURSEGROUP_ATTRIBUTE_VALUES(courseGroupId ?? ""),
    queryFn: () => coursegroupAttributesApi.fetchValues(Number(courseGroupId)),
    enabled: !!courseGroupId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: QUERIES_KEYS.COURSEGROUP_ATTRIBUTE_VALUES(courseGroupId ?? ""),
    });
  };

  return {
    invalidate,
    isLoading,
    values: data?.data ?? [],
  };
};

export const useCourseGroupAttributesAll = () => {
  const { isLoading, data } = useQuery({
    queryKey: QUERIES_KEYS.COURSEGROUP_ATTRIBUTES_ALL(),
    queryFn: () => coursegroupAttributesApi.fetch(0, 1_000_000),
  });

  return {
    isLoading,
    allAttributes: data?.data.data ?? [],
  };
};
